import {isNil} from 'lodash';
import {useMemo} from 'react';
import styled from 'styled-components';
import {LoadingBar} from '@/components/LoadingBar';
import {Typography} from '@/components/Typography';
import {MEDALS} from '@/constants';
import {SalesSummary} from '@/map/@types';
import {useMapDataSource} from '@/map/api';
import {Flexbox, FlexFullGrow, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {withComma} from '@/utils';
import {
  GrayBox, GrayCenterBox, SectionBlock, SectionSubTitle, SectionTitle, SubValueText, ValueText
} from '../../StyledComponents';
import {EMPTY_ITEM_TYPES, HOUSE_SALES_TYPES} from '../constants';
import {EmptyItem} from '../EmptyItem';
import {SalesDataProps} from '../interface';

type RankingData = Array<SalesSummary & {idx: number}>;

export const HallSalesCompare = ({
  isPending,
  error,
  data: rawdata,
}: SalesDataProps) => {
  const {data: dataSource} = useMapDataSource();
  const metadata = useMemo(
    () =>
      dataSource?.find(
        (source) =>
          source.key === 'kodata' && source.data.indexOf('매출 정보') !== -1
      ),
    [dataSource]
  );

  const data = useMemo(() => {
    return rawdata?.map((i, idx) => ({
      ...(i?.salesSummary || {}),
      idx,
    }));
  }, [rawdata]);

  return (
    <SectionBlock>
      <SectionTitle>홀 매출</SectionTitle>
      <br />
      <SectionSubTitle>{`${metadata?.source || '카드사'}∙${
        metadata?.baseDate || ''
      }`}</SectionSubTitle>
      <PaddingBox top={16}>
        {isPending ? (
          <GrayCenterBox>
            <LoadingBar color={palette.textSecondary} />
          </GrayCenterBox>
        ) : error ? (
          <Typography variant="medium12" color={palette.textSecondary}>
            분석 실패
          </Typography>
        ) : !data ? (
          <EmptyItem type={EMPTY_ITEM_TYPES.Sales} />
        ) : (
          <HallSalesChartBox items={data} />
        )}
      </PaddingBox>
    </SectionBlock>
  );
};

const renderRanking = (rankingData: RankingData, type: string) => {
  const firstItem = (rankingData[0] as any)[type];

  if (firstItem < 0 || isNil(firstItem)) {
    return (
      <Typography variant="medium12" color={palette.gray600} align="center">
        매출 정보가 없습니다.
      </Typography>
    );
  }

  return rankingData.map((i, ranking) => (
    <Rank key={i.idx}>
      {MEDALS[ranking]}
      <Typography variant="medium14" color={palette.textPrimary}>
        비교 상권 {i.idx + 1}
      </Typography>
      <FlexFullGrow />
      <div>
        <ValueText>{withComma((i as any)[type])}</ValueText>
        <SubValueText>{type === 'cardSalesAvg' ? ' 원' : ' 천원'}</SubValueText>
      </div>
    </Rank>
  ));
};

const getSortedItems = (items: RankingData, key: string) => {
  return [...items].sort((a, b) => (b as any)[key] - (a as any)[key]);
};

const HallSalesChartBox = ({items}: {items: RankingData}) => {
  const rankings = useMemo(
    () =>
      HOUSE_SALES_TYPES.reduce((acc: {[key: string]: RankingData}, {key}) => {
        acc[key] = getSortedItems(items, key);

        return acc;
      }, {}),
    [items]
  );

  return (
    <SalesSection>
      {HOUSE_SALES_TYPES.map(({key, label}) => (
        <RankingBox key={key}>
          <Typography variant="medium14">
            <Typography color={palette.primary500} fontWeight="700">
              {label}
            </Typography>
            이 높은 상권은?
          </Typography>
          <GrayBox>
            <RankingBox>{renderRanking(rankings[key], key)}</RankingBox>
          </GrayBox>
        </RankingBox>
      ))}
    </SalesSection>
  );
};

const SalesSection = styled(FlexFullGrow)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Rank = styled(Flexbox)`
  gap: 4px;
`;

const RankingBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
